import type { Creneau, PossibleDuJour, TacheEnvoyee } from '../../partage/api/types';
import { dureeNonPosee, echeanceLe, libereDesChoses, MOT_NE_RENTRE_PLUS } from '../../partage/vocabulaire';
import { dureeLisible, jourEnToutesLettres } from './datesDeLAgenda';

/**
 * « Préparer ma journée » d'après sa maquette (chantier `revue-design`, ticket 06,
 * `design/agenda.html`, décision d'Igor du 2026-10-03). Sans rendu.
 *
 * La journée en cours de préparation est une liste de choix, dans l'ordre : chacun a son heure
 * fixée (« 14:00 ») ou `null`, « à la suite ». Une journée neuve arrive **vide** (décision
 * d'Igor du 2026-10-04, B) : chaque tâche s'ajoute à la main. Le serveur range (les heures, le
 * temps libre, les rendez-vous) et donne l'ordre des possibles ; ici, seulement les gestes sur
 * la liste et ce qui se montre, sans rien reclasser.
 */

export interface Choix {
  id: string;
  /** L'heure fixée, « 14:00 » ; `null` : à la suite. */
  fixee: string | null;
}

/** Le temps pris par les choix. */
export function minutesChoisies(choisis: readonly Choix[], minutesDe: ReadonlyMap<string, number>): number {
  return choisis.reduce((n, c) => n + (minutesDe.get(c.id) ?? 0), 0);
}

/**
 * Le temps pris par la journée qu'on compose et ce qui reste du temps qu'on se donne, à
 * l'ordinateur comme au téléphone (chantier `nettoyage-journee`, ticket 02). `durees` : la durée
 * de chaque tâche connue de l'écran, `[id, minutes]` ; donnée deux fois, la dernière l'emporte.
 */
export function tempsDeLaJournee(
  choisis: readonly Choix[],
  durees: Iterable<readonly [string, number]>,
  tempsMin: number
): { utilise: number; reste: number } {
  const utilise = minutesChoisies(choisis, new Map(durees));
  return { utilise, reste: tempsMin - utilise };
}

/** La largeur de la jauge du temps pris, pleine au plus. */
export function largeurDeLaJauge(utilise: number, tempsMin: number): string {
  return `${Math.min(100, (100 * utilise) / tempsMin)}%`;
}

/** Ce qui fait venir une tâche, à l'ordinateur : ce qu'elle libère, son échéance. Jamais « presse ». */
export function raisonsDeLaTache(p: Pick<PossibleDuJour, 'libere' | 'echeance'>): string[] {
  return [p.libere > 0 ? libereDesChoses(p.libere) : null, p.echeance ? echeanceLe(jourEnToutesLettres(p.echeance)) : null].filter(
    (x): x is string => !!x
  );
}

/**
 * Une tâche à ajouter, à l'ordinateur : « Refaire la cuisine · 1 h · libère 2 choses ». Rangée
 * sous son projet, la ligne ne le redit pas ; trop longue pour ce qui reste, elle le dit.
 */
export function ceQuElleEst(p: PossibleDuJour, avecProjet: boolean, trop: boolean): string {
  return [
    avecProjet ? p.dossier?.titre : null,
    p.estimee ? dureeNonPosee(dureeLisible(p.minutes)) : dureeLisible(p.minutes),
    ...raisonsDeLaTache(p),
    trop ? MOT_NE_RENTRE_PLUS : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * « À ajouter » : les premières possibles non choisies qui tiennent dans ce qui reste, dans
 * l'ordre du serveur, et combien d'autres sont trop longues pour ce qui reste.
 */
export function aAjouter(
  possibles: readonly PossibleDuJour[],
  choisis: readonly Choix[],
  reste: number,
  combien = 3
): { montrees: PossibleDuJour[]; tropLongues: number } {
  const pris = new Set(choisis.map((c) => c.id));
  const libres = possibles.filter((p) => !pris.has(p.id));
  return {
    montrees: libres.filter((p) => p.minutes <= reste).slice(0, combien),
    tropLongues: libres.filter((p) => p.minutes > reste).length,
  };
}

/** Fixer l'heure d'une tâche, ou la remettre à la suite (`null`). */
export function fixer(choisis: readonly Choix[], id: string, heure: string | null): Choix[] {
  return choisis.map((c) => (c.id === id ? { ...c, fixee: heure } : c));
}

/** Ajouter à la fin, à la suite ; jamais deux fois. */
export function ajouter(choisis: readonly Choix[], id: string): Choix[] {
  return choisis.some((c) => c.id === id) ? [...choisis] : [...choisis, { id, fixee: null }];
}

export function retirer(choisis: readonly Choix[], id: string): Choix[] {
  return choisis.filter((c) => c.id !== id);
}

/** Ce que les routes attendent : l'identifiant seul à la suite, `{ id, heure }` à une heure fixée. */
export function versLaRoute(choisis: readonly Choix[]): TacheEnvoyee[] {
  return choisis.map((c) => (c.fixee ? { id: c.id, heure: c.fixee } : c.id));
}

/**
 * Une journée enregistrée relue : chaque tâche que le serveur dit `fixee` garde son heure, les
 * autres sont à la suite. Sans quoi la réenregistrer la déferait (compte rendu du ticket 05 de
 * `flottaison-suite`).
 */
export function choixDeLaJournee(taches: readonly { bulleId: string; heure: string; fixee?: boolean }[]): Choix[] {
  return taches.map((t) => ({ id: t.bulleId, fixee: t.fixee ? t.heure : null }));
}

/** Le texte sans accents ni majuscules, pour chercher. */
function aplati(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLocaleLowerCase('fr-FR')
    .trim();
}

/** « Chercher une tâche » : celles dont le titre contient le texte, dans l'ordre reçu. */
export function chercher(possibles: readonly PossibleDuJour[], texte: string): PossibleDuJour[] {
  const cherche = aplati(texte);
  return cherche ? possibles.filter((p) => aplati(p.titre).includes(cherche)) : [...possibles];
}

/**
 * Une heure tapée au clavier : seulement entière (« 14:05 ») et dans la journée ; `null`
 * sinon, et le champ revient à l'heure d'avant (fiche React et Vite, « une date tapée au
 * clavier s'enregistre à chaque chiffre »). `incomplet` : le navigateur dit le champ à moitié
 * rempli.
 */
export function heureTapee(valeur: string, incomplet: boolean): string | null {
  if (incomplet) return null;
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(valeur) ? valeur : null;
}

/** Combien de tâches ont leur heure fixée, combien sont à la suite, d'après le rangement. */
export function heuresDuJour(creneaux: readonly Pick<Creneau, 'sorte' | 'fixee'>[]): { fixees: number; aLaSuite: number } {
  const taches = creneaux.filter((c) => c.sorte === 'tache');
  const fixees = taches.filter((c) => c.fixee).length;
  return { fixees, aLaSuite: taches.length - fixees };
}
